// E-Ticaret › Formlar kullanım rehberi. Metinler 'guides' çeviri alanında.
import type { TFunction } from 'i18next';
import type { GuideContent } from '../../components/common/UsageGuideDialog';

const G = 'ecommerce.forms';

export const ecommerceFormsGuide = (t: TFunction): GuideContent => ({
  layout: 'tabbed',
  title: t(`${G}.title`, 'Formlar nasıl hazırlanır?'),
  intro: t(`${G}.intro`, 'Sitenizde göstermek istediğiniz formları (örn. Franchise Başvurusu, İletişim) burada tanımlarsınız. Hazırladığınız formu "Sayfalar" ekranında zengin metin editörünün üzerindeki "Form Ekle" düğmesiyle bir sayfaya yerleştirirsiniz; gelen yanıtları "Gelen Cevaplar" sekmesinden okursunuz. Haritada bir bölgeye tıklayarak o adıma geçebilirsiniz.'),
  regionLabels: {
    header: t(`${G}.regions.header`, 'Formlar / QR Üret / Yeni Form'),
    tabs: t(`${G}.regions.tabs`, 'Form sekmeleri'),
    body: t(`${G}.regions.body`, 'Form Tanımı / Gelen Cevaplar'),
    footer: t(`${G}.regions.footer`, 'Sil / Kaydet'),
  },
  navItems: [
    t(`${G}.nav.edit`, 'Form Tanımı'),
    t(`${G}.nav.submissions`, 'Gelen Cevaplar'),
  ],
  steps: [
    {
      region: 'header',
      title: t(`${G}.s1.title`, '1. Form oluşturun'),
      text: t(`${G}.s1.text`, '"Yeni Form" düğmesi hazır alanlarla (Ad Soyad, E-posta, Telefon, Mesajınız) yeni bir form açar; her form üstte bir sekme olur. "Form Tanımı" sekmesinde Form Başlığı (zorunlu), Kısa Ad (slug; boşsa başlıktan üretilir), Açıklama (formun üstünde görünür), Gönder Butonu Yazısı, Başarı Mesajı ve Sıralama alanlarını doldurun; "Aktif" anahtarıyla formu açıp kapatın.'),
      tips: [
        t(`${G}.s1.tip1`, 'Form "Kaydet"e basılana kadar kaydedilmez. Kaydedilmemiş formda "Gelen Cevaplar" sekmesi ve "QR Üret" kullanılamaz.'),
        t(`${G}.s1.tip2`, '"QR Üret" kayıtlı form için bir QR kod oluşturur; okutulunca bu form doğrudan açılır.'),
      ],
    },
    {
      region: 'body',
      title: t(`${G}.s2.title`, '2. Alan ekleyin'),
      text: t(`${G}.s2.text`, '"Form Alanları" bölümünde "Alan Ekle" ile yeni alan ekleyin ve "Alan Türü"nü seçin: Tek Satır Metin, Uzun Metin, E-posta, Telefon, Sayı, Şehir + İlçe, İl + Şube, Resim Yükleme (çoklu), Açılır Liste, Tek Seçim, Çoklu Seçim, Tarih. Her alanın Etiketi zorunludur; Yer Tutucu ve Yardım metni isteğe bağlıdır. "Zorunlu" anahtarı açık alan doldurulmadan form gönderilemez. Yukarı / Aşağı oklarla sırayı değiştirin, çöp kutusuyla alanı silin.'),
      tips: [
        t(`${G}.s2.tip1`, 'Açılır Liste, Tek Seçim ve Çoklu Seçim alanlarına en az bir seçenek yazılmalıdır (her satıra bir tane; / , ; ile de ayırabilirsiniz).'),
        t(`${G}.s2.tip2`, 'Şehir + İlçe sitede otomatik şehir ve ilçe listesi gösterir. İl + Şube önce ili, sonra o ildeki şubeleri listeler; şubeler E-Ticaret → Şubeler ekranından gelir. Bu iki türde seçenek girmeniz gerekmez.'),
        t(`${G}.s2.tip3`, 'Resim Yükleme alanında kullanıcı birden fazla JPG, PNG veya WEBP yükleyebilir.'),
        t(`${G}.s2.tip4`, '"Bölüm Ekle" formu başlıklarla bölümlere ayırır (Kişisel Bilgiler, İletişim Bilgileri vb. ya da özel başlık). Formda bölüm dışında en az bir alan olmalıdır.'),
      ],
    },
    {
      region: 'body',
      title: t(`${G}.s3.title`, '3. Sözleşme onayı bağlayın'),
      text: t(`${G}.s3.text`, '"Onay Ekle" menüsünden KVKK Aydınlatma Metni, Mesafeli Satış Sözleşmesi, Ön Bilgilendirme Formu, Gizlilik Politikası, ticari ileti onayı veya özel onay kutusu ekleyin. "Yasal Sözleşmeler\'den bağla" listesinden bir belge seçin; kullanıcı onay metnindeki bağlantıya tıklayınca o sözleşmenin içeriği açılır.'),
      tips: [
        t(`${G}.s3.tip1`, 'Alternatif olarak "Özel sayfa seç" ile Sayfalar\'daki bir sayfayı ya da "Elle yazılan metin" alanını kullanabilirsiniz. Sözleşme veya sayfa seçiliyse önce o gösterilir; bulunamazsa elle yazılan metin kullanılır.'),
        t(`${G}.s3.tip2`, 'Henüz yasal belge yoksa önce E-Ticaret → Yasal Sözleşmeler ekranından oluşturun. Onay kutuları zorunlu olarak eklenir.'),
      ],
    },
    {
      region: 'body',
      title: t(`${G}.s4.title`, '4. Bildirim e-postası'),
      text: t(`${G}.s4.text`, '"Bildirim E-postası (opsiyonel)" alanına bir adres yazarsanız, form her gönderildiğinde içerik bu adrese de e-postayla iletilir. Boş bırakırsanız yanıtlar yalnız "Gelen Cevaplar"da görünür.'),
      tips: [
        t(`${G}.s4.tip1`, 'Geçersiz bir e-posta adresi yazılırsa bildirim gönderilmez; yanıt yine kaydedilir.'),
      ],
    },
    {
      region: 'body',
      title: t(`${G}.s5.title`, '5. Gelen cevaplar'),
      text: t(`${G}.s5.text`, '"Gelen Cevaplar" sekmesi bu forma gelen yanıtları listeler; üstte Toplam ve Okunmamış sayıları görünür. Bir yanıtı açıp okuyun; zarf simgesiyle okundu / okunmadı olarak işaretleyin, çöp kutusuyla onay sorduktan sonra kalıcı olarak silin. Yeni yanıtlar için Yenile düğmesine basın.'),
    },
    {
      region: 'footer',
      title: t(`${G}.s6.title`, '6. Kaydedin veya silin'),
      text: t(`${G}.s6.text`, '"Kaydet" formu ve alanlarını kaydeder; başlığı veya etiketi eksik alan, seçeneksiz liste varsa uyarı verir. "Sil" formu onay sorduktan sonra kalıcı olarak siler; formun gelen yanıtları da etkilenebilir.'),
      tips: [
        t(`${G}.s6.tip1`, 'Başlıktaki (i) düğmesi bu rehberi her zaman yeniden açar.'),
      ],
    },
  ],
});
